import React from 'react';
import { motion } from 'framer-motion';
import { FaBriefcase, FaCheckCircle } from 'react-icons/fa';
import './Experience.css';

const Experience = () => {
  const experiences = [
    {
      period: '2025 – Present',
      company: 'Software Company',
      role: 'Software Quality Assurance Engineer',
      type: 'Full-time',
      responsibilities: [
        'Develop and execute comprehensive test plans for web and mobile applications',
        'Design and implement automated test frameworks using Selenium and PyTest',
        'Perform API testing using Postman and REST Assured with complete endpoint validation',
        'Conduct performance testing with JMeter to ensure system scalability',
        'Collaborate with development teams in Agile/Scrum environment',
        'Create and maintain detailed test documentation and defect reports',
        'Mentor junior QA team members on testing best practices',
        'Integrate automated tests into CI/CD pipelines using Jenkins'
      ],
      tools: ['Selenium', 'PyTest', 'Postman', 'JMeter', 'Jira', 'Git', 'Jenkins'],
      achievements: [
        'Reduced regression testing time by 60% through automation',
        'Achieved 95% test coverage across critical modules',
        'Identified and resolved 200+ defects before production'
      ]
    },
    {
      period: '2025 – Present',
      company: 'Freelance QA Consultant',
      role: 'Quality Assurance Engineer',
      type: 'Freelance',
      responsibilities: [
        'Provided QA consulting services for multiple clients across different industries',
        'Performed manual and automated testing for e-commerce, healthcare, and fintech applications',
        'Created comprehensive test strategies and test plans from scratch',
        'Conducted cross-browser and responsive testing for web applications',
        'Performed security testing and vulnerability assessments',
        'Delivered detailed test reports and quality metrics to stakeholders',
        'Set up testing processes and quality standards for startup projects'
      ],
      tools: ['Selenium', 'Postman', 'TestRail', 'Jira', 'Git', 'Browser DevTools'],
      achievements: [
        'Successfully delivered QA services for 15+ projects',
        'Improved client software quality by 40% on average',
        'Built reusable test automation frameworks for clients'
      ]
    }
  ];

  return (
    <section id="experience" className="experience">
      <div className="container">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title">Professional Experience</h2>
          <p className="section-subtitle">
            My journey in software quality assurance and testing
          </p>
        </motion.div>

        <div className="timeline">
          {experiences.map((exp, index) => (
            <motion.div
              key={index}
              className="timeline-item"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
            >
              <div className="timeline-marker">
                <FaBriefcase />
              </div>

              <div className="timeline-content">
                <div className="timeline-header">
                  <div className="timeline-period">{exp.period}</div>
                  <span className="timeline-type">{exp.type}</span>
                </div>

                <h3 className="timeline-role">{exp.role}</h3>
                <h4 className="timeline-company">{exp.company}</h4>

                <div className="timeline-section">
                  <h5>Key Responsibilities</h5>
                  <ul className="responsibilities-list">
                    {exp.responsibilities.map((resp, idx) => (
                      <li key={idx}>
                        <FaCheckCircle className="list-icon" />
                        <span>{resp}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="timeline-section">
                  <h5>Tools & Technologies</h5>
                  <div className="tools-list">
                    {exp.tools.map((tool, idx) => (
                      <span key={idx} className="tool-tag">{tool}</span>
                    ))}
                  </div>
                </div>

                <div className="timeline-section">
                  <h5>Key Achievements</h5>
                  <ul className="achievements-list">
                    {exp.achievements.map((achievement, idx) => (
                      <li key={idx}>
                        🏆 {achievement}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
